import React from 'react'
import {
  Form, Field, FieldArray, ErrorMessage,
} from 'formik'
import styled, { css } from 'styled-components'
import {
  faTimes, faArrowUp, faArrowDown, faCheck,
} from '@fortawesome/free-solid-svg-icons'

import { theme } from '../constants/theme'
import { VARIANT_SECONDARY, VARIANT_WARNING } from '../constants/variants'
import { GRID_BLOCK_TYPE } from '../constants/gridBlockType'
import ColoredBlockIcon from '../img/notation couleurs.png'
import CustomBlockIcon from '../img/customCriterions.png'
import CommentBlockIcon from '../img/Comment.png'


import { Form as FormK } from './common/Form'
import { Margin } from './common/Margin'
import { Label, Menu, Body } from './common/Typography'
import {
  Button, LinkButton, OrangeButton, DummyButton,
} from './common/Button'
import { ConfirmButton } from './common/ConfirmButton'
import { Icon } from './common/Icon'
import { Trad } from './common/Trad'
import { CustomFormikInput } from './common/Input'
import { FormError } from './common/FormError'
import { Block as Section } from './common/Block'
import { Bin } from './icons/Bin'
import { AddItem } from './icons/AddItem'


const baseColoredBlock = {
  type: GRID_BLOCK_TYPE.colored,
  criterions: [{ text: '' }],
  name: '',
}

const baseCustomBlock = {
  type: GRID_BLOCK_TYPE.custom,
  customCriterions: [{
    name: '',
    value0: '',
    value1: '',
    value2: '',
  }],
  name: '',
}

const baseCommentBlock = {
  type: GRID_BLOCK_TYPE.comment,
  comments: [{
    name: '',
    help: '',
  }],
  name: '',
}

const baseGradesBlock = {
  type: GRID_BLOCK_TYPE.grades,
  grades: [{}],
}

export const GridForm = ({
  onSubmit, path, initialValues, disabled, onSave, saveSuccess,
}) => (
  <FormK
    onSubmit={onSubmit}
    path={path}
    initialValues={initialValues}
    formik
    render={({ values, dirty, isSubmitting }) => (
      <Form>
        <Label
          upper
          bold
          variant={VARIANT_SECONDARY}
          tradId="name"
          fontSize={theme.typography.h3}
        />
        <Margin v={15} />
        <Section border>
          <FieldWrapper>
            <Field
              component={CustomFormikInput}
              placeholder="gridNamePlaceholder"
              name="name"
              disabled={disabled}
              fontSize={theme.typography.h3}
            />
            <ErrorMessage
              name="name"
              component={(err) => <FormError error={err.children} />}
            />
          </FieldWrapper>
        </Section>
        <Margin v={30} />

        <Label
          upper
          bold
          variant={VARIANT_SECONDARY}
          tradId={`blockTypes.${baseGradesBlock.type}`}
          fontSize={theme.typography.h3}
        />
        <Margin v={15} />
        <FieldArray
          name="grades"
          render={(arrayHelpers) => (
            <Section border>
              {values.grades.map((grade, index) => (
                <div key={index}>
                  <Row key={grade}>
                    <GradeWrapper>
                      <Field
                        disabled={disabled}
                        component={CustomFormikInput}
                        name={`grades.${index}.text`}
                        placeholder="gradeNamePlaceholder"
                      />
                    </GradeWrapper>
                    {values.grades.length > 1 && (
                      <RemoveIcon disabled={disabled} arrayHelpers={arrayHelpers} index={index} />
                    )}
                  </Row>
                  <ErrorMessage
                    name={`grades.${index}.text`}
                    component={(err) => <FormError error={err.children} />}
                  />
                  <Margin v={5} />
                </div>
              ))}
              {!disabled && values.grades.length < 3 && (
                <AddElement
                  arrayHelpers={arrayHelpers}
                  tradId="addGrade"
                  initialValue={{ text: '' }}
                />
              )}
            </Section>
          )}
        />
        <Margin v={30} />

        <FieldArray
          name="blocks"
          render={(arrayHelpers) => (
            <div>
              <Label upper bold variant={VARIANT_SECONDARY} tradId="blocks" fontSize={theme.typography.h3} />
              <Margin v={5} />
              <Margin v={10} />
              <div>
                {values.blocks.map((block, index) => (
                  <Block
                    name={block.type}
                    key={block.type + index}
                    i={index}
                    fields={block}
                    disabled={disabled}
                    values={values.blocks}
                    arrayHelpers={arrayHelpers}
                  />
                ))}
              </div>
              {!disabled && (
                <Section shadow>
                  <Menu tradId="addBlocks" bold />
                  <Margin v={10} />
                  <AddBlockButton
                    onClick={() => arrayHelpers.push(baseColoredBlock)}
                    icon={ColoredBlockIcon}
                    label="addColoredBlock"
                  />
                  <AddBlockButton
                    onClick={() => arrayHelpers.push(baseCustomBlock)}
                    icon={CustomBlockIcon}
                    label="addCustomCriterionBlock"
                  />
                  <AddBlockButton
                    onClick={() => arrayHelpers.push(baseCommentBlock)}
                    icon={CommentBlockIcon}
                    label="addCommentBlock"
                  />
                </Section>
              )}
            </div>
          )}
        />

        <Margin v={30} />
        {!disabled && (
          <div>
            <Button
              onClick={() => {
                onSave(values)
              }}
              tradId="saveDraft"
              variant={VARIANT_SECONDARY}
              icon={saveSuccess && !dirty && <Icon icon={faCheck} />}
            />
            <Margin h={10} />
            <Button
              type="submit"
              tradId="publish"
              variant={VARIANT_SECONDARY}
              disabled={isSubmitting}
            />
          </div>
        )}
      </Form>
    )}
  />
)

const Block = ({
  name, i, fields, disabled, values, arrayHelpers,
}) => {
  const { type } = fields

  return (
    <BlockWrapper key={name}>
      {!disabled && (
        <BlockControlWrapper>
          <NoStyleButton
            onClick={() => arrayHelpers.swap(i, i - 1)}
            icon={<Icon icon={faArrowUp} />}
            disabled={i === 0}
            variant={VARIANT_SECONDARY}
          />
          <CustomConfirmButton
            titleTrad="categoryDeleteConfirm"
            onConfirm={() => arrayHelpers.remove(i)}
            icon={<Icon icon={faTimes} />}
            variant={VARIANT_WARNING}
            disabled={values.length < 1}
          />
          <NoStyleButton
            onClick={() => arrayHelpers.swap(i, i + 1)}
            icon={<Icon icon={faArrowDown} />}
            disabled={i === values.length - 1}
            variant={VARIANT_SECONDARY}
          />
        </BlockControlWrapper>
      )}
      <BlockContentWrapper>
        <Label
          upper
          bold
          variant={VARIANT_SECONDARY}
          tradId={`blockTypes.${type}`}
          fontSize={theme.typography.h3}
        />
        <Margin v={15} />
        <Section border>
          <FieldWrapper>
            <Field
              disabled={disabled}
              component={CustomFormikInput}
              name={`blocks.${i}.name`}
              label="name"
              placeholder="addBlockName"
            />
            <ErrorMessage
              name={`blocks.${i}.name`}
              component={(err) => <FormError error={err.children} />}
            />
            <Margin v={10} />
          </FieldWrapper>
          {type === GRID_BLOCK_TYPE.colored && (
            <FieldArray
              name={`blocks.${i}.criterions`}
              render={(arrayHelpers) => (
                <div>
                  <Margin v={10} />
                  <Label bold variant={VARIANT_SECONDARY} tradId="criterions" />
                  <Margin h={10} />
                  <Circle color={theme.colors.green} />
                  <Margin h={3} />
                  <Circle color={theme.colors.warningYellow} />
                  <Margin h={3} />
                  <Circle color={theme.colors.red} />
                  <Margin h={3} />
                  <Circle color={theme.colors.grayDark} />
                  {fields.criterions.map((criterionName, index) => (
                    <CriterionWrapper
                      key={criterionName + index}
                    >
                      <Row>
                        <FieldWrapper>
                          <Field
                            disabled={disabled}
                            component={CustomFormikInput}
                            name={`blocks.${i}.criterions.${index}.text`}
                            placeholder="critionPlaceholder"
                          />
                          <ErrorMessage
                            name={`blocks.${i}.criterions.${index}.text`}
                            component={(err) => <FormError error={err.children} />}
                          />
                        </FieldWrapper>
                        {fields.criterions.length > 1 && (
                          <RemoveIcon disabled={disabled} arrayHelpers={arrayHelpers} index={index} />
                        )}
                      </Row>
                    </CriterionWrapper>
                  ))}
                  {!disabled && (
                    <AddElement
                      arrayHelpers={arrayHelpers}
                      tradId="addCriterion"
                      initialValue={{ text: '' }}
                    />
                  )}
                </div>
              )}
            />
          )}
          {type === GRID_BLOCK_TYPE.custom && (
            <FieldArray
              name={`blocks.${i}.customCriterions`}
              render={(arrayHelpers) => (
                <div>
                  {fields.customCriterions.map((criterionName, index) => (
                    <>
                      <Margin v={10} />
                      <Section border key={criterionName + index}>
                        <Row>
                          <FieldWrapper>
                            <Field
                              disabled={disabled}
                              label="criterionName"
                              component={CustomFormikInput}
                              name={`blocks.${i}.customCriterions.${index}.name`}
                              placeholder="critionPlaceholder"
                            />
                            <ErrorMessage
                              name={`blocks.${i}.customCriterions.${index}.name`}
                              component={(err) => <FormError error={err.children} />}
                            />
                          </FieldWrapper>
                          {fields.customCriterions.length > 1 && (
                            <RemoveIcon disabled={disabled} arrayHelpers={arrayHelpers} index={index} />
                          )}
                        </Row>
                        <Margin v={10} />
                        <div>
                          <Label bold variant={VARIANT_SECONDARY} tradId="customCriterionsLabel" />
                          <Margin h={10} />
                          <Circle color={theme.colors.grayDark} />
                          <Margin h={3} />
                          <Circle color={theme.colors.grayDark} />
                          <Margin h={3} />
                          <Circle color={theme.colors.grayDark} />
                        </div>
                        <FieldWrapper>
                          <Field
                            disabled={disabled}
                            component={CustomFormikInput}
                            name={`blocks.${i}.customCriterions.${index}.value0`}
                            placeholder="insufficient"
                          />
                          <ErrorMessage
                            name={`blocks.${i}.customCriterions.${index}.value0`}
                            component={(err) => <FormError error={err.children} />}
                          />
                        </FieldWrapper>
                        <FieldWrapper>
                          <Field
                            disabled={disabled}
                            component={CustomFormikInput}
                            name={`blocks.${i}.customCriterions.${index}.value1`}
                            placeholder="average"
                          />
                          <ErrorMessage
                            name={`blocks.${i}.customCriterions.${index}.value1`}
                            component={(err) => <FormError error={err.children} />}
                          />
                        </FieldWrapper>
                        <FieldWrapper>
                          <Field
                            disabled={disabled}
                            component={CustomFormikInput}
                            name={`blocks.${i}.customCriterions.${index}.value2`}
                            placeholder="good"
                          />
                          <ErrorMessage
                            name={`blocks.${i}.customCriterions.${index}.value2`}
                            component={(err) => <FormError error={err.children} />}
                          />
                        </FieldWrapper>
                        <Margin h={3} />
                      </Section>
                    </>
                  ))}
                  {!disabled && (
                    <AddElement
                      arrayHelpers={arrayHelpers}
                      tradId="addCriterion"
                      initialValue={{
                        name: '', value0: '', value1: '', value2: '',
                      }}
                    />
                  )}
                </div>
              )}
            />
          )}
          {type === GRID_BLOCK_TYPE.comment && (
            <FieldArray
              name={`blocks.${i}.comments`}
              render={(arrayHelpers) => (
                <div>
                  <Margin v={10} />
                  {fields.comments.map((criterionName, index) => (
                    <Section border key={criterionName + index}>
                      <Row>
                        <FieldWrapper>
                          <Field
                            disabled={disabled}
                            placeholder="commentName"
                            component={CustomFormikInput}
                            name={`blocks.${i}.comments.${index}.name`}
                          />
                          <ErrorMessage
                            name={`blocks.${i}.comments.${index}.name`}
                            component={(err) => <FormError error={err.children} />}
                          />
                        </FieldWrapper>
                        {fields.comments.length > 1 && (
                          <RemoveIcon disabled={disabled} arrayHelpers={arrayHelpers} index={index} />
                        )}
                      </Row>
                      <FieldWrapper>
                        <Field
                          disabled={disabled}
                          placeholder="commentHelp"
                          component={CustomFormikInput}
                          name={`blocks.${i}.comments.${index}.help`}
                        />
                        <ErrorMessage
                          name={`blocks.${i}.comments.${index}.help`}
                          component={(err) => <FormError error={err.children} />}
                        />
                      </FieldWrapper>
                      <Margin h={5} />
                    </Section>
                  ))}
                  {!disabled && (
                    <AddElement
                      arrayHelpers={arrayHelpers}
                      tradId="addComment"
                      initialValue={{ name: '', help: '' }}
                    />
                  )}
                </div>
              )}
            />
          )}
        </Section>
      </BlockContentWrapper>
    </BlockWrapper>
  )
}

const RemoveIcon = ({ disabled, arrayHelpers, index }) => (!disabled ? (
  <OrangeButton
    icon={<Bin />}
    onClick={() => arrayHelpers.remove(index)}
  />
) : null)

const AddBlockButton = ({
  icon, label, ...rest
}) => (
  <div>
    <CustomButton
      {...rest}
    >
      <AddBlockButtonWrapper>
        <ImageWrapper>
          <img src={icon} alt="none" />
        </ImageWrapper>
        <Margin h={10} />
        <Trad id={label} />
      </AddBlockButtonWrapper>
    </CustomButton>
  </div>
)

const AddElement = ({ arrayHelpers, tradId, initialValue }) => (
  <AddElementWrapper>
    <Margin v={10} />
    <DummyButton
      icon={<AddItem />}
      onClick={() => arrayHelpers.push(initialValue)}
    />
    <Margin h={15} />
    <Body tradId={tradId} />
  </AddElementWrapper>
)

const AddBlockButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`

const ImageWrapper = styled.div`
  width: 27px;
  display: inline-block;
`

const Circle = styled.div`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 3px ${({ color }) => color} solid;
`

const noStyle = css`
padding: 0;
border:none;
`
const NoStyleButton = styled(Button)`
  ${noStyle}
`
const CustomConfirmButton = styled(ConfirmButton)`
${noStyle}
`
const BlockControlWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 5px;
`
const BlockContentWrapper = styled.div`
  flex: 1;
  padding-left: 7px;
`
const BlockWrapper = styled.div`
  display: flex;
  margin-bottom: 20px;
`

const CriterionWrapper = styled.div`
display: flex;
align-items: center;
`
const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  &>* {
    flex: 1;
  }
  width: 100%
  max-width: 500px;
`

const GradeWrapper = styled.div`
  display: flex;
  width: 100%
  max-width: 500px;
  justify-content: space-between;
`

const CustomButton = styled(LinkButton).attrs({
  upper: false,
  bold: false,
})`
  padding: 5px;
  color: black;
  ${theme.transition()};
  &:hover {
    background: ${theme.colors.grayLight};
  }
`

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`

const AddElementWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${theme.colors.grayDark};
`
